import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { Panel } from "../components/Surface";
import { Icon } from "../app/Icon";
import { SpecGrid, Tags, VerifiedPill, GatedNotice, MetaFooter, RouteLine, shortId } from "../components/HeroDetail";
import { SaveButton } from "../components/ResultCard";
import { labelize } from "../lib/format";
import {
  getLoad, bidOnLoad, loadBids, awardBid, declineBid, setLoadStatus,
  rateLabel, ratePerMile, dimsLabel, LOAD_BID_TYPES,
  type LoadDetail as Load, type LoadBid,
} from "../lib/loads";

export function LoadDetail() {
  const { id } = useParams();
  const { account } = useAuth();
  const { active } = useProfiles();
  const [load, setLoad] = useState<Load | null>(null);
  const [bids, setBids] = useState<LoadBid[]>([]);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isOwner = Boolean(load && active && load.poster_profile_id === active.id);
  const isBidder = active ? LOAD_BID_TYPES.includes(active.type) : false;

  async function loadLoad() {
    if (!id) return;
    setLoad((await getLoad(id)).load);
  }
  useEffect(() => {
    void loadLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    if (isOwner && id) loadBids(id).then((r) => setBids(r.bids));
  }, [isOwner, id]);

  async function onBid() {
    if (!load || !active) return;
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setMsg("Enter a bid amount.");
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await bidOnLoad(load.id, active.id, value, note || undefined);
      setMsg("Bid submitted.");
      setAmount("");
      setNote("");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Could not bid.");
    } finally {
      setBusy(false);
    }
  }
  async function refreshBids() {
    if (id) setBids((await loadBids(id)).bids);
  }
  async function onAward(bidId: string) {
    await awardBid(bidId);
    await loadLoad();
    await refreshBids();
  }
  async function onDecline(bidId: string) {
    await declineBid(bidId);
    await refreshBids();
  }
  async function toggleStatus() {
    if (!load) return;
    const next = load.status === "open" ? "closed" : "open";
    await setLoadStatus(load.id, next);
    setLoad({ ...load, status: next });
  }

  if (!load) return <p className="p-10 font-mono text-sm text-muted">Loading…</p>;
  const canManage = load.status === "open";
  const perMile = ratePerMile(load.rate, load.rate_type, load.trip_miles);
  const showGate = isBidder && !isOwner && !account?.identity_verified;

  return (
    <div className="grid gap-6">
      <Link to="/loads" className="flex items-center gap-1 text-sm font-bold text-accent-strong">
        <Icon name="loads" className="h-4 w-4" /> Load board
      </Link>

      {/* Hero card */}
      <div className="grid gap-5 rounded-card border border-line bg-surface p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid gap-2">
            <RouteLine origin={load.origin} destination={load.destination} miles={load.trip_miles} />
            <VerifiedPill label={labelize(load.poster_type)} />
          </div>
          <div className="grid justify-items-end gap-0.5">
            <span className="font-mono text-2xl font-black text-verified">{rateLabel(load.rate, load.rate_type)}</span>
            {perMile && <span className="font-mono text-xs text-muted">{perMile}</span>}
            {load.status !== "open" && <span className="font-mono text-xs uppercase text-danger">{load.status}</span>}
          </div>
        </div>

        <SpecGrid
          items={[
            ["Equipment", labelize(load.equipment_type)],
            ["Weight", load.weight ? `${load.weight.toLocaleString()} lb` : ""],
            ["Distance", load.trip_miles ? `${load.trip_miles.toLocaleString()} mi` : ""],
            ["Rate/mi", perMile],
            ["Category", labelize(load.category)],
            ["Load", labelize(load.full_partial)],
            ["Dimensions", dimsLabel(load.length_ft, load.width_ft, load.height_ft)],
            ["Pieces", load.pieces],
            ["Temp", load.temperature],
            ["Commodity", load.commodity],
          ]}
        />

        <Tags items={[load.equipment_type, load.category, load.full_partial, ...load.special_requirements]} />

        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          {([
            ["Pickup", [load.pickup_date, load.pickup_time].filter(Boolean).join(" ")],
            ["Delivery", [load.delivery_date, load.delivery_time].filter(Boolean).join(" ")],
            ["Flexible", load.flexible_dates ? "Yes" : ""],
            ["Reference", load.reference_number],
          ] as [string, string | null][]).filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="grid gap-0.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted">{k}</span>
              <span className="font-mono text-sm text-ink">{v}</span>
            </div>
          ))}
        </div>

        {load.description && (
          <section className="grid gap-2">
            <h2 className="text-[11px] font-bold uppercase tracking-widest text-muted">Notes</h2>
            <p className="max-w-prose whitespace-pre-wrap text-sm leading-relaxed">{load.description}</p>
          </section>
        )}

        {/* Bidder actions */}
        {isBidder && !isOwner && (
          <div className="grid gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="w-32 rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm"
                type="number"
                placeholder="Amount $"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <input
                className="min-w-[10rem] flex-1 rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm"
                placeholder="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <Button onClick={onBid} disabled={busy || load.status !== "open"}>
                {load.status === "open" ? "Bid" : "Closed"}
              </Button>
              <SaveButton kind="loads" id={load.id} />
            </div>
            {showGate && <GatedNotice>Verify to bid — complete identity + credential verification.</GatedNotice>}
            {msg && <p className="font-mono text-sm text-muted">{msg}</p>}
          </div>
        )}
      </div>

      <MetaFooter
        items={[
          ["Load ID", shortId("LD", load.id)],
          ["Posted", new Date(load.created_at).toLocaleDateString()],
          ["Rate type", labelize(load.rate_type)],
          ["Status", load.status.toUpperCase()],
        ]}
      />

      {/* Poster manages bids */}
      {isOwner && (
        <Panel
          title={`Bids (${bids.length})`}
          action={load.status !== "awarded" ? <Button variant="ghost" onClick={toggleStatus}>{load.status === "open" ? "Close load" : "Reopen"}</Button> : undefined}
        >
          {bids.length === 0 ? (
            <p className="font-mono text-sm text-muted">No bids yet.</p>
          ) : (
            <ul className="grid gap-3">
              {bids.map((b) => (
                <li key={b.id} className="border-t border-line pt-3 first:border-t-0 first:pt-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/p/${b.bidder_profile_id}`} className="font-bold hover:underline">{b.display_name ?? "—"}</Link>
                    <span className="font-mono text-sm text-verified">${b.amount.toLocaleString()}</span>
                    <span className="font-mono text-xs uppercase text-muted">{b.status}</span>
                  </div>
                  {b.note && <p className="mt-1 text-sm text-muted">{b.note}</p>}
                  {canManage && (b.status === "submitted" || b.status === "viewed") && (
                    <div className="mt-2 flex gap-2">
                      <Button onClick={() => onAward(b.id)}>Award</Button>
                      <Button variant="ghost" onClick={() => onDecline(b.id)}>Decline</Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}
    </div>
  );
}
